import { Icon } from "@material-ui/core";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import { useCatalogStore } from "application/contexts/catalog/CatalogStore";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import {
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import { ICartContext } from "modules/cart/domain/interfaces/IUseCaseCart";
import { IOrderItem } from "modules/cart/domain/models/IOrderItem";
import { ProdutoTipo } from "modules/cart/domain/models/IProductType";
import { useCartStore } from "modules/cart/infra/store/CartStore";
import React, {
  CSSProperties,
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { hexToRGBA, moneyMask, roundValue } from "utils/utils";
import styles from "./CartItem.module.scss";
import { useTranslation } from "i18n/useTranslation";

interface ICartItemProps {
  orderItem: IOrderItem;
  hideZeroPrice?: boolean;
}

export const CartItem: FC<ICartItemProps> = memo(
  ({ orderItem, hideZeroPrice }) => {
    const [addCartItem, removeCartItem, setShowCart, showCart] = useCartStore(
      (state: ICartContext) => [
        state.addCartItem,
        state.removeCartItem,
        state.setShowCart,
        state.showCart,
      ]
    );
    const { t, getProductName } = useTranslation();
    const { openProductDetail } = useCatalogStore();
    const { resolution } = useScreenStore();
    const { kiosk } = useKiosk();

    const [showCompositions, setShowCompositions] = useState(false);

    useEffect(() => {
      if (showCart) {
        setShowCompositions(false);
      }
    }, [showCart]);

    const onPressToggleComposition = useCallback(() => {
      setShowCompositions((prev) => !prev);
    }, []);

    const onPressRemoveHandle = useCallback(() => {
      if (orderItem.productType === ProdutoTipo.Kilo) {
        removeCartItem(orderItem.id, orderItem.quantity);
      } else {
        removeCartItem(orderItem.id, 1);
      }
    }, [
      orderItem.id,
      orderItem.productType,
      orderItem.quantity,
      removeCartItem,
    ]);

    const onPressAddHandle = useCallback(() => {
      if (
        orderItem.productType === ProdutoTipo.Kilo ||
        (orderItem.compositions?.length &&
          orderItem.compositions.some((i) => i.compositionItems.length > 0)) ||
        orderItem?.productGeneralObservation
      ) {
        openProductDetail(orderItem.productId);
        setShowCart(false);
      } else {
        addCartItem(orderItem.id, 1);
      }
    }, [addCartItem, openProductDetail, orderItem, setShowCart]);

    const hasComposition = useMemo(() => {
      return (
        orderItem.orderComposition &&
        orderItem.orderComposition?.length > 0 &&
        orderItem.orderComposition.some((it) =>
          it.orderCompositionItems.some((i) => i.description)
        )
      );
    }, [orderItem.orderComposition]);

    const onImgError = (ev: React.ChangeEvent<HTMLImageElement>) => {
      ev.target.src = require("../../../../../assets/images/no-image.png");
    };

    return (
      <div
        id={styles.CartItem}
        style={
          {
            "--primary-color": "var(--primary)",
          } as CSSProperties
        }
      >
        <div className={styles.imgContainer}>
          <img
            src={orderItem.thumbnailUrl}
            alt="product"
            onErrorCapture={onImgError}
          />
          {orderItem.productType !== ProdutoTipo.Kilo && (
            <div>
              <span
                style={
                  {
                    "--color": hexToRGBA(
                      kiosk?.themeColor ?? kiosk?.primaryColor ?? "#FFF",
                      0.3
                    ),
                  } as CSSProperties
                }
              >
                {orderItem.quantity}
              </span>
            </div>
          )}
        </div>

        <div className={styles.infoContainer}>
          <p
            style={{
              maxWidth: resolution === ResolutionType.FULL_HD ? 700 : 400,
            }}
          >{`${
            orderItem.productType === ProdutoTipo.Kilo
              ? `${getProductName(orderItem)}  (${moneyMask(orderItem.realPrice)}/Kg)`
              : getProductName(orderItem)
          }`}</p>
          <p>
            {`${
              orderItem.productType === ProdutoTipo.Kilo
                ? `${orderItem.quantity}Kg - `
                : ""
            }${
              hideZeroPrice && orderItem.price === 0
                ? "R$ 0,00"
                : moneyMask(orderItem.realPrice * orderItem.quantity)
            }`}
          </p>
          <div onClick={onPressToggleComposition}>
            {hasComposition && (
              <p>
                {t("cart_view.complements.title")}
                <span>{`(+ ${moneyMask(
                  roundValue(orderItem.totalPrice - orderItem.realPrice)
                )})`}</span>
                {showCompositions ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </p>
            )}
            {showCompositions && (
              <div>
                {orderItem.orderComposition
                  ?.filter(
                    (items) =>
                      !(
                        (items.description === "Observação" &&
                          items.orderCompositionItems[0]?.description === "") ||
                        items.orderCompositionItems.length === 0
                      )
                  )
                  .map((composition) =>
                    composition.orderCompositionItems.filter(
                      (item) =>
                        item?.description !== "" ||
                        item?.description !== undefined ||
                        item?.description !== null ||
                        item?.quantity === 0
                    ) ? (
                      <div key={composition.id}>
                        <span>{composition.description}</span>
                        <div>
                          {composition.orderCompositionItems.map((item, k) => (
                            <div
                              key={`${k}-${item.compositionId}`}
                              className={
                                item.isProductGeneralObservation
                                  ? styles.obsItem
                                  : undefined
                              }
                            >
                              {item?.productId
                                ? `${item.quantity} - ${item.description.slice(
                                    0,
                                    resolution === ResolutionType.FULL_HD
                                      ? 50
                                      : 20
                                  )}${
                                    item.description.length >
                                    (resolution === ResolutionType.FULL_HD
                                      ? 50
                                      : 20)
                                      ? "..."
                                      : ""
                                  } - ${moneyMask(
                                    (item.quantity -
                                      item.quantityFreeSelected) *
                                      item.price
                                  )}`
                                : item.description}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      ""
                    )
                  )}
              </div>
            )}
          </div>
        </div>

        <div className={styles.buttonsContainer}>
          {orderItem.productType !== ProdutoTipo.TaxaServico && (
            <>
              <Icon onClick={onPressAddHandle}>add</Icon>
              <Icon onClick={onPressRemoveHandle}>remove</Icon>
            </>
          )}
        </div>
      </div>
    );
  }
);
